import React from "react";

import { Icon, Tooltip } from "minimum-ui/core";
import { ICONS } from "minimum-ui/core/Icon/icons";
import { color } from "minimum-ui/tokens";
import styled, { css } from "styled-components";

import Button from "./Button";

export type ToggleSegmentOption = {
  label?: string;
  value: string;
  indicatorColor?: string;
  tooltip?: string;
  startIcon?: keyof typeof ICONS;
  endIcon?: keyof typeof ICONS;
};

type ToggleSegmentProps = {
  options: ToggleSegmentOption[];
  value?: string | null;
  onChange?: (newVal: string) => void;
  className?: string;
};

const ToggleSegment: React.FC<ToggleSegmentProps> = ({
  options,
  value,
  onChange,
  className,
}) => {
  return (
    <Container className={className}>
      {options.map((opt) => {
        return (
          <Tooltip title={opt.tooltip} key={opt.value}>
            <ToggleButton
              dataTestId={`${opt.value}-based-button`}
              size="small"
              key={opt.value}
              $selected={opt.value === value}
              onClick={() => {
                onChange?.(opt.value);
              }}
            >
              {opt.startIcon && <Icon icon={opt.startIcon} size="sm" />}
              {opt.label || opt.value}
              {opt.endIcon && <Icon icon={opt.endIcon} size="sm" />}
              {opt.indicatorColor && <Indicator color={opt.indicatorColor} />}
            </ToggleButton>
          </Tooltip>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  background-color: ${color.neutral[100]};
  border: 1px solid ${color.border.default};
  border-radius: 0.25rem;
  padding: 0.125rem;
  display: inline-flex;
  flex-direction: row;
  gap: 0.25rem;
`;

const ToggleButton = styled(Button)<{ $selected?: boolean }>`
  border-radius: 0.25rem;
  background-color: transparent;
  border: 1px solid transparent;
  --color: ${color.neutral[500]};
  white-space: nowrap;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  transition: 0.3s ease-in-out;

  ${({ $selected }) =>
    $selected &&
    css`
      --color: ${color.neutral[500]};
      background-color: ${color.neutral[300]};
      border-color: ${color.neutral[400]};
    `}
  &:hover {
    --color: ${color.neutral[100]};
    border-color: ${color.neutral[300]};
    background-color: ${color.neutral[400]};
  }
`;

const Indicator: React.FC<{ color: string }> = ({ color }) => {
  if (!color) {
    return null;
  }

  return <Icon icon="circle" size="2xs" color={color} />;
};

export default ToggleSegment;
